import { AcademicCapIcon, BuildingOfficeIcon, IdentificationIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { States } from '../../../../constants';
import { formatDate3 } from '../../../../utils';
import type { JsonInput } from '../../../UI/JsonForm/JsonInputRenderer/types';
import {
  clientApplicationConstructionType,
  clientApplicationProjectStatus,
  clientApplicationProjectType,
} from '../../ClientApplication/constants';
import { acquisitionTypes, constructionTypes, dealTypes, projectByRight, siteControlStatuses } from '../../HandHousingForm/constants';
import type { ApplicationFormStep } from '../types';

const applicantInformation: JsonInput[] = [
  {
    type: 'input',
    props: {
      name: 'orgInfo.businessName',
      label: 'Business Name',
    },
  },
  {
    type: 'input',
    props: {
      name: 'orgInfo.businessAddress',
      label: 'Business Address',
    },
  },
  {
    type: 'input',
    props: {
      name: 'orgInfo.businessCity',
      label: 'Business City',
    },
  },
  {
    type: 'select',
    props: {
      name: 'orgInfo.state',
      label: 'State',
      options: States.map((state) => ({
        label: state.name,
        value: state.abbreviation,
      })),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'orgInfo.zipCode',
      label: 'Zip / Postal Code',
      maskedComponent: 'zipCode',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'orgInfo.businessPhone',
      label: 'Business Phone',
      maskedComponent: 'phone',
    },
  },
  {
    type: 'input',
    props: {
      name: 'orgInfo.website',
      label: 'URL / Web Address (Optional)',
    },
  },
  {
    type: 'date',
    props: {
      name: 'orgInfo.establishedDate',
      label: 'Date established / Date of Incorporation',
      max: formatDate3(new Date().toISOString(), 'yyyy-mm-dd'),
    },
  },
  {
    type: 'input',
    props: {
      name: 'orgInfo.leadContact',
      label: 'Lead Contact',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'orgInfo.phone',
      label: 'Phone',
      maskedComponent: 'phone',
    },
  },
  {
    type: 'input',
    props: {
      name: 'orgInfo.email',
      label: 'E-mail',
    },
  },

  {
    type: 'masked',
    gridColSpan: 'col-span-6',
    props: {
      name: 'orgInfo.yearsInDevelopment',
      label: 'How many years have you been in development ?',
      maskedComponent: 'numeric',
    },
  },
  {
    type: 'select',
    gridColSpan: 'col-span-6',
    props: {
      name: 'orgInfo.ownOperateAffordableProperties',
      label: 'Do you currently Own/Operate any affordable properties ?',
      options: [
        {
          label: 'Yes',
          value: 'Yes',
        },
        {
          label: 'No',
          value: 'No',
        },
      ],
    },
  },
  {
    type: 'select',
    gridColSpan: 'col-span-6',
    props: {
      name: 'orgInfo.isHandMember',
      label: 'Are you a HAND Member ?',
      options: [
        {
          label: 'Yes',
          value: 'Yes',
        },
        {
          label: 'No',
          value: 'No',
        },
      ],
    },
  },
  {
    type: 'select',
    gridColSpan: 'col-span-6',
    props: {
      name: 'orgInfo.haveWorkedWithGreystone',
      label: 'Have you worked with Greystone in the past ?',
      options: [
        {
          label: 'Yes',
          value: 'Yes',
        },
        {
          label: 'No',
          value: 'No',
        },
      ],
    },
  },
  {
    type: 'select',
    gridColSpan: 'col-span-6',
    props: {
      name: 'orgInfo.canHelp',
      label: 'How can Equity in Action help you?',
      options: [
        { label: 'Bridge Financing', value: 'Bridge Financing' },
        { label: 'Permanent Financing', value: 'Permanent Financing' },
        {
          label: 'Construction Financing',
          value: 'Construction Financing',
        },
        {
          label: 'I need help with the process',
          value: 'I need help with the process',
        },
        { label: "I'm not sure", value: "I'm not sure" },
      ],
    },
  },
  {
    type: 'masked',
    gridColSpan: 'col-span-6',
    props: {
      name: 'orgInfo.capitalAmount',
      label: 'For Projects seeking capital, how much capital is needed ?',
      maskedComponent: 'currency',
    },
  },
  {
    type: 'checkbox-group',
    gridColSpan: 'col-span-12',
    props: {
      name: 'orgInfo.greystoneHelp',
      label: 'How else can Greystone help ?',
      hasOthers: true,
      options: [
        {
          label: 'CRE Portfolio Reviews',
          value: 'CRE Portfolio Reviews',
        },
        {
          label: 'Restructuring Advisory',
          value: 'Restructuring Advisory',
        },
        { label: 'LIHTC Expertise', value: 'LIHTC Expertise' },
        { label: 'Bond Financing', value: 'Cond Financing' },
      ],
    },
  },
];
const socialDesignation: JsonInput[] = [
  {
    type: 'radio-group',
    gridColSpan: 'col-span-12',
    props: {
      name: 'orgInfo.socialDesignation',
      hasOthers: true,
      options: [
        { label: 'Minority Owned', value: 'Minority Owned' },
        { label: 'Woman Owned', value: 'Woman Owned' },
        {
          label: 'Disadvantaged Business Enterprise',
          value: 'Disadvantaged Business Enterprise',
        },
        { label: 'Veteran Owned', value: 'Veteran Owned' },
        { label: 'Local Business', value: 'Local Business' },
      ],
    },
  },
];
const legalStatus: JsonInput[] = [
  {
    type: 'radio-group',
    gridColSpan: 'col-span-12 -mt-5',
    props: {
      name: 'orgInfo.legalStatus',
      hasOthers: true,
      options: [
        { label: 'LLC', value: 'llc' },
        { label: 'LLP', value: 'llp' },
        { label: 'LLLP', value: 'lllp' },
        { label: 'Corporation', value: 'corporation' },
        { label: 'Tax Exempt 501 (c)3', value: 'tax-exempt-501-c-3' },
      ],
    },
  },
];
const sponsorInformation: JsonInput[] = [
  {
    type: 'empty',
    props: {} as JsonInput['props'],
    isArray: true,
    minArrayLength: 1,
    gridColSpan: 'col-span-12',
    arrayName: 'orgInfo.sponsors',
    inputs: [
      {
        type: 'input',
        props: {
          name: 'name',
          label: 'Name',
        },
      },
      {
        type: 'input',
        props: {
          name: 'address',
          label: 'Address',
        },
      },
      {
        type: 'input',
        props: {
          name: 'city',
          label: 'City',
        },
      },
      {
        type: 'select',
        props: {
          name: 'state',
          label: 'State',
          options: States.map((state) => ({
            label: state.name,
            value: state.abbreviation,
          })),
        },
      },
      {
        type: 'masked',
        props: {
          name: 'zipCode',
          label: 'Zip /  Postal Code',
          maskedComponent: 'zipCode',
        },
      },
      {
        type: 'masked',
        props: {
          name: 'phone',
          label: 'Phone',
          maskedComponent: 'phone',
        },
      },
      {
        type: 'input',
        props: {
          name: 'email',
          label: 'E-mail',
        },
      },
      {
        type: 'input',
        props: {
          name: 'ownershipPercent',
          label: 'Ownership %',
          type: 'number',
        },
      },
    ],
  },
];

const priorProject: JsonInput[] = [
  {
    type: 'empty',
    props: {} as JsonInput['props'],
    isArray: true,
    minArrayLength: 1,
    gridColSpan: 'col-span-12',
    arrayName: 'orgExperience.priorProjects',
    inputs: [
      {
        type: 'input',
        props: {
          name: 'address',
          label: 'Address',
        },
      },
      {
        type: 'input',
        props: {
          name: 'city',
          label: 'City',
        },
      },
      {
        type: 'select',
        props: {
          name: 'state',
          label: 'State',
          options: States.map((state) => ({
            label: state.name,
            value: state.abbreviation,
          })),
        },
      },
      {
        type: 'masked',
        props: {
          name: 'zipCode',
          label: 'Zip / Postal Code',
          maskedComponent: 'zipCode',
        },
      },
      {
        type: 'select',
        props: {
          name: 'projectType',
          label: 'Project Type',
          options: Object.keys(clientApplicationProjectType).map((key) => ({
            label: clientApplicationProjectType[key],
            value: key,
          })),
        },
      },
      {
        type: 'select',
        props: {
          name: 'constructionType',
          label: 'Construction Type',
          options: Object.keys(clientApplicationConstructionType).map((key) => ({
            label: clientApplicationConstructionType[key],
            value: key,
          })),
        },
      },
      {
        type: 'date',
        props: {
          name: 'completionDate',
          label: 'Completion Date',
          max: formatDate3(new Date().toISOString(), 'yyyy-mm-dd'),
        },
      },
      {
        type: 'select',
        props: {
          name: 'status',
          label: 'Status',
          options: Object.keys(clientApplicationProjectStatus).map((key) => ({
            label: clientApplicationProjectStatus[key],
            value: key,
          })),
        },
      },
      {
        type: 'masked',
        props: {
          name: 'projectCost',
          label: 'Total Project Cost',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'file',
        gridColSpan: 'col-span-12',
        props: {
          name: 'pictures',
          label: 'Pictures',
          multiple: true,
        },
      },
    ],
  },
];

const potentialProject: JsonInput[] = [
  {
    type: 'input',
    gridColSpan: 'col-span-12',
    props: {
      name: 'potentialProject.streetAddress',
      label: 'Street Address',
    },
  },

  {
    type: 'input',
    props: {
      name: 'potentialProject.city',
      label: 'City',
    },
  },
  {
    type: 'select',
    props: {
      name: 'potentialProject.state',
      label: 'State',
      options: States.map((state) => ({
        label: state.name,
        value: state.abbreviation,
      })),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'potentialProject.zipCode',
      label: 'Zip / Postal Code',
      maskedComponent: 'zipCode',
    },
  },
  {
    type: 'select',
    props: {
      name: 'potentialProject.dealType',
      label: 'Deal Type',
      options: dealTypes.map((dt) => ({
        label: dt,
        value: dt,
      })),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'potentialProject.proposedUnits',
      label: 'Proposed Number of Units',
      maskedComponent: 'numeric',
    },
  },
  {
    type: 'select',
    props: {
      name: 'potentialProject.byRight',
      label: 'Is Proposed Project "By-Right"',
      options: projectByRight.map((pbr) => ({
        label: pbr,
        value: pbr,
      })),
    },
  },
  {
    type: 'select',
    props: {
      name: 'potentialProject.constructionType',
      label: 'Construction Type',
      options: constructionTypes.map((ct) => ({
        label: ct,
        value: ct,
      })),
    },
  },
  {
    type: 'select',
    props: {
      name: 'potentialProject.acquisitionType',
      label: 'Acquisition Type',
      options: acquisitionTypes.map((at) => ({
        label: at,
        value: at,
      })),
    },
  },
  {
    type: 'select',
    props: {
      name: 'potentialProject.siteControlStatus',
      label: 'Status of Site Control',
      options: siteControlStatuses.map((scs) => ({
        label: scs,
        value: scs,
      })),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'potentialProject.acquisitionCost',
      label: 'Estimated Acquisiton Cost',
      maskedComponent: 'currency',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'potentialProject.developmentCost',
      label: 'Estimated Development Cost',
      maskedComponent: 'currency',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'potentialProject.preDevelopmentCost',
      label: 'Estimated Pre-Development Cost',
      maskedComponent: 'currency',
    },
  },
  {
    type: 'textarea',
    gridColSpan: 'col-span-12',
    props: {
      name: 'potentialProject.moreInfo',
      label: 'More Information About Project',
    },
  },
  //{
  //  type: 'file',
  //  gridColSpan: 'col-span-12',
  //  props: {
  //    name: 'potentialProject.additionalDocs',
  //    label: 'Additional Docs',
  //    multiple: true,
  //  },
  //},
  {
    type: 'checkbox-group',
    gridColSpan: 'col-span-12',
    props: {
      name: 'potentialProject.financingUse',
      label: 'Use of Financing',
      hasOthers: true,
      options: [
        { label: 'Acquisition', value: 'Acquisition' },
        { label: 'Construction', value: 'Construction' },
        { label: 'Refinance', value: 'Refinance' },
        { label: 'Re-Capitalization', value: 'Re-Capitalization' },
      ],
    },
  },
];

export const equityInActionApplicationSteps: ApplicationFormStep[] = [
  {
    title: 'Information',
    icon: IdentificationIcon,
    sections: [
      {
        id: 'applicantInformation',
        title: 'Applicant Information',
        inputs: applicantInformation,
      },
      {
        id: 'socialDesignation',
        title: 'Social Designation',
        inputs: socialDesignation,
      },
      {
        id: 'legalStatus',
        title: 'Tax / Legal Status',
        inputs: legalStatus,
      },
      {
        id: 'sponsorInformation',
        title: 'Sponsor Information',
        inputs: sponsorInformation,
      },
    ],
  },
  {
    title: 'Potential Project',
    icon: BuildingOfficeIcon,
    sections: [
      {
        id: 'potentialProject',
        title: 'Potential Project',
        inputs: potentialProject,
      },
    ],
  },
  {
    title: 'Experience',
    icon: AcademicCapIcon,
    sections: [
      {
        id: 'priorProject',
        title: 'Prior Project',
        inputs: priorProject,
      },
    ],
  },
  {
    title: 'Review',
    icon: InformationCircleIcon,
    sections: [],
    isReview: true,
  },
];
